import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { 
  Calculator, 
  LogOut, 
  FileText, 
  Shield, 
  Wallet,
  TrendingUp,
  Settings,
  ChevronRight,
  Loader2,
  RefreshCw,
  Laptop,
  Key,
  Star,
  ShieldCheck,
  Umbrella,
  Gift,
  Sparkles,
  Calendar,
  Trophy,
  UserCheck,
  Briefcase,
  FileStack,
  Users,
  Palette,
  Globe,
  ShieldAlert,
  BookOpen,
  Building2,
  Mail,
  Layout,
  AlertTriangle,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import ProfileDialog from "@/components/ProfileDialog";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user, logout, getAuthHeaders } = useAuth();
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const [seeding2026, setSeeding2026] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [stats, setStats] = useState({
    irs_tables: 0,
    ss_rates: 0,
    costs: 0,
    margins: 0,
    equipment: 0,
    licenses: 0,
    irs_youth: 0,
    custom_benefits: 0,
    fiscal_pending: 0,
  });
  const [fiscalChecking, setFiscalChecking] = useState(false);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const [irsRes, ssRes, costsRes, marginsRes, equipRes, licensesRes, youthRes, customBenefitsRes, pendingRes] = await Promise.all([
        axios.get(`${API}/irs`, { headers: getAuthHeaders() }),
        axios.get(`${API}/ss`, { headers: getAuthHeaders() }),
        axios.get(`${API}/costs`, { headers: getAuthHeaders() }),
        axios.get(`${API}/margins`, { headers: getAuthHeaders() }),
        axios.get(`${API}/equipment`, { headers: getAuthHeaders() }),
        axios.get(`${API}/licenses`, { headers: getAuthHeaders() }).catch(() => ({ data: [] })),
        axios.get(`${API}/irs-jovem`, { headers: getAuthHeaders() }),
        axios.get(`${API}/custom-benefits`, { headers: getAuthHeaders() }),
        axios.get(`${API}/admin/fiscal/pending`, { headers: getAuthHeaders() }).catch(() => ({ data: [] })),
      ]);
      
      setStats({
        irs_tables: irsRes.data.length,
        ss_rates: ssRes.data.length,
        costs: costsRes.data.length,
        margins: marginsRes.data.length,
        equipment: equipRes.data.length,
        licenses: licensesRes.data.length,
        irs_youth: youthRes.data.length,
        custom_benefits: customBenefitsRes.data.length,
        fiscal_pending: (pendingRes.data || []).length,
      });
    } catch (error) {
      console.error("Error loading stats:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSeedData = async () => {
    setSeeding(true);
    try {
      await axios.post(`${API}/admin/seed`, {}, { headers: getAuthHeaders() });
      toast.success("Dados iniciais carregados com sucesso!");
      loadStats();
    } catch (error) {
      const message = error.response?.data?.detail || "Erro ao carregar dados";
      toast.error(message);
    } finally {
      setSeeding(false);
    }
  };

  const handleSeedIRS2026 = async () => {
    setSeeding2026(true);
    try {
      const response = await axios.post(`${API}/admin/seed-irs-2026`, {}, { headers: getAuthHeaders() });
      toast.success(response.data.message);
      loadStats();
    } catch (error) {
      const message = error.response?.data?.detail || "Erro ao carregar dados 2026";
      toast.error(message);
    } finally {
      setSeeding2026(false);
    }
  };

  const runFiscalCheck = async () => {
    setFiscalChecking(true);
    try {
      const r = await axios.post(`${API}/admin/fiscal/check-updates`, {}, { headers: getAuthHeaders() });
      const n = r.data?.created_count ?? 0;
      if (n === 0) toast.success("Nenhuma alteração detectada — tabelas alinhadas com o canonical.");
      else toast.warning(`${n} nova(s) atualização(ões) fiscal(is) detectada(s). Reveja e aprove abaixo.`);
      await loadStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao verificar atualizações");
    } finally { setFiscalChecking(false); }
  };

  const applyAllFiscal = async () => {
    if (!window.confirm("Aplicar TODAS as atualizações fiscais pendentes? Simulações antigas mantêm-se resolvidas pelo ano em que foram criadas.")) return;
    setFiscalChecking(true);
    try {
      const list = await axios.get(`${API}/admin/fiscal/pending`, { headers: getAuthHeaders() });
      let applied = 0, failed = 0;
      for (const p of (list.data || [])) {
        try {
          await axios.post(`${API}/admin/fiscal/pending/${p.id}/apply`, {}, { headers: getAuthHeaders() });
          applied++;
        } catch { failed++; }
      }
      if (failed) toast.warning(`Aplicadas ${applied}, falharam ${failed}.`);
      else toast.success(`${applied} atualização(ões) aplicada(s).`);
      await loadStats();
    } catch { toast.error("Erro ao aplicar atualizações"); }
    finally { setFiscalChecking(false); }
  };


  const handleLogout = () => {
    logout();
    navigate("/");
    toast.success("Sessão terminada");
  };

  const menuItems = [
    {
      title: "Tabelas IRS",
      description: "Gerir tabelas de retenção IRS",
      icon: FileText,
      href: "/admin/irs",
      count: stats.irs_tables,
      color: "bg-[var(--brand-primary)]"
    },
    {
      title: "IRS Jovem",
      description: "Configurar escalões IRS Jovem",
      icon: Umbrella,
      href: "/admin/irs-jovem",
      count: stats.irs_youth,
      color: "bg-[var(--brand-primary)]"
    },
    {
      title: "Segurança Social",
      description: "Taxas de contribuição SS",
      icon: Shield,
      href: "/admin/ss",
      count: stats.ss_rates,
      color: "bg-[var(--brand-primary)]"
    },
    {
      title: "Custos Operacionais",
      description: "Custos salariais e operacionais",
      icon: Wallet,
      href: "/admin/costs",
      count: stats.costs,
      color: "bg-[var(--brand-primary)]"
    },
    {
      title: "Calendário Laboral",
      description: "Feriados PT e dias úteis por mês",
      icon: Calendar,
      href: "/admin/calendar",
      count: null,
      color: "bg-[var(--brand-primary)]"
    },
    {
      title: "Margens",
      description: "Configuração de margens",
      icon: TrendingUp,
      href: "/admin/margins",
      count: stats.margins,
      color: "bg-[var(--brand-primary)]"
    },
    {
      title: "Equipamentos",
      description: "Computadores e dispositivos",
      icon: Laptop,
      href: "/admin/equipment",
      count: stats.equipment,
      color: "bg-[var(--brand-primary)]"
    },
    {
      title: "Licenças",
      description: "Software e licenças mensais",
      icon: Key,
      href: "/admin/licenses",
      count: stats.licenses,
      color: "bg-[var(--brand-primary)]"
    },
    {
      title: "Situações Especiais",
      description: "Sub-fees ou % específicas por cliente",
      icon: Star,
      href: "/admin/client-special-fees",
      count: null,
      color: "bg-[var(--brand-primary)]"
    },
    {
      title: "Utilizadores & Grupos",
      description: "Gestão de acessos e permissões granulares",
      icon: ShieldCheck,
      href: "/admin/users",
      count: null,
      color: "bg-[var(--brand-primary)]"
    },
    {
      title: "Layouts do Simulador",
      description: "Preset default global e por grupo (Compacta · Completa · Comparativa)",
      icon: Layout,
      href: "/admin/layout-presets",
      count: null,
      color: "bg-[var(--brand-primary)]"
    },
    {
      title: "Permissões de Negócio",
      description: "Aprovadores por grau de responsabilidade (emails)",
      icon: ShieldAlert,
      href: "/admin/approvers",
      count: null,
      color: "bg-[var(--brand-primary)]"
    },
    {
      title: "Benefícios",
      description: "Limites de benefícios flexíveis",
      icon: Gift,
      href: "/admin/benefits",
      count: 1,
      color: "bg-[var(--brand-primary)]"
    },
    {
      title: "Benefícios Custom",
      description: "Benefícios configuráveis",
      icon: Sparkles,
      href: "/admin/custom-benefits",
      count: stats.custom_benefits,
      color: "bg-[var(--brand-primary)]"
    },
    {
      title: "Margem de Negócio",
      description: "Prémios por margem (3 grupos)",
      icon: Trophy,
      href: "/admin/premiums",
      count: null,
      color: "bg-[var(--brand-primary)]"
    },
    {
      title: "Prémios Fixos",
      description: "Prémios fixos por utilizador (email)",
      icon: Trophy,
      href: "/admin/fixed-bonuses",
      count: null,
      color: "bg-[var(--brand-primary)]"
    },
    {
      title: "Fee Tiers Perm",
      description: "Escalões de comissão Perm",
      icon: UserCheck,
      href: "/admin/perm-fee-tiers",
      count: null,
      color: "bg-[var(--brand-primary)]"
    },
    {
      title: "Fee Perm Fixo",
      description: "Fee Perm fixo por utilizador (email)",
      icon: UserCheck,
      href: "/admin/fixed-perm-fees",
      count: null,
      color: "bg-[var(--brand-primary)]"
    },
    {
      title: "Cores da Margem",
      description: "Configurar cores por faixa de margem",
      icon: Palette,
      href: "/admin/margin-colors",
      count: null,
      color: "bg-[var(--brand-primary)]"
    },
    {
      title: "Custos Internacionais",
      description: "Valores padrão (Viagem, Visto, Advogados, Reagrup. Familiar)",
      icon: Globe,
      href: "/admin/international-costs",
      count: null,
      color: "bg-[var(--brand-primary)]"
    },
    {
      title: "Configurações do Negócio",
      description: "Toggle IHT sujeito a IRS e outras políticas fiscais",
      icon: Settings,
      href: "/admin/business-settings",
      count: null,
      color: "bg-[var(--brand-primary)]"
    },
    {
      title: "Proposta PDF",
      description: "Definições da proposta financeira",
      icon: FileText,
      href: "/admin/proposal-settings",
      count: null,
      color: "bg-[var(--brand-primary)]"
    },
    {
      title: "Funções / Cargos",
      description: "Lista de funções para consultores",
      icon: Briefcase,
      href: "/admin/job-functions",
      count: null,
      color: "bg-[var(--brand-primary)]"
    },
    {
      title: "Templates",
      description: "Templates de simulação pré-configurados",
      icon: FileStack,
      href: "/admin/templates",
      count: null,
      color: "bg-[var(--brand-primary)]"
    },
    {
      title: "Candidatos",
      description: "Gerir links e dados de candidatos",
      icon: Users,
      href: "/candidates",
      count: null,
      color: "bg-[var(--brand-primary)]"
    },
    {
      title: "Clientes",
      description: "Gerir clientes (prazo pagamento, meses faturáveis) e histórico de rates",
      icon: Building2,
      href: "/admin/clients",
      count: null,
      color: "bg-[var(--brand-primary)]"
    },
    {
      title: "Template Email",
      description: "Pré-formatação do email enviado com as propostas (link + PDF)",
      icon: Mail,
      href: "/admin/email-templates",
      count: null,
      color: "bg-[var(--brand-primary)]"
    },
    {
      title: "Regimes Fiscais PT 2026",
      description: "Documentação das taxas e fórmulas (IRC 21%, IVA art. 53.º, SS 21.4%, etc.)",
      icon: BookOpen,
      href: "/admin/tax-rates",
      count: null,
      color: "bg-[var(--brand-primary)]"
    }
  ];

  return (
    <div className="min-h-screen bg-background" data-testid="admin-dashboard">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-slate-200/50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="bg-white rounded-lg px-3 py-1.5">
              <img src="/evolutt_logo_white.png" alt="Evolutt" className="h-10 w-auto" />
            </div>
            <p className="text-xs text-muted-foreground">Backoffice Admin</p>
          </div>
          
          <div className="flex items-center gap-4">
            <Link to="/simulator">
              <Button variant="outline" size="sm" data-testid="simulator-link">
                <Calculator className="w-4 h-4 mr-2" />
                Simulador
              </Button>
            </Link>
            <button
              type="button"
              onClick={() => setShowProfile(true)}
              className="text-sm text-muted-foreground hover:text-brand-700 transition-colors cursor-pointer"
              title="Editar o meu perfil"
              data-testid="open-profile-btn"
            >
              <span className="font-medium text-foreground">{user?.name}</span>
            </button>
            <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="logout-btn">
              <LogOut className="w-4 h-4 mr-2" />
              Sair
            </Button>
          </div>
        </div>
      </header>

      <ProfileDialog open={showProfile} onOpenChange={setShowProfile} />

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Page Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-manrope font-extrabold text-3xl text-foreground">Dashboard Admin</h1>
            <p className="text-muted-foreground mt-1">Gestão de tabelas, taxas e configurações</p>
          </div>
          <Button 
            onClick={handleSeedData} 
            disabled={seeding}
            className="bg-brand-600 text-white hover:bg-brand-700 font-bold"
            data-testid="seed-data-btn"
          >
            {seeding ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                A carregar...
              </>
            ) : (
              <>
                <RefreshCw className="w-4 h-4 mr-2" />
                Carregar Dados Iniciais
              </>
            )}
          </Button>
          
          <Button 
            onClick={handleSeedIRS2026}
            disabled={seeding2026}
            variant="outline"
            className="border-blue-500 text-blue-600 hover:bg-blue-50"
            data-testid="seed-irs-2026-btn"
          >
            {seeding2026 ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                A carregar...
              </>
            ) : (
              <>
                <Calendar className="w-4 h-4 mr-2" />
                IRS/SS 2026
              </>
            )}
          </Button>
        </div>

        {/* ============ Fiscal Auto-Update banner ============ */}
        <div
          className={`mb-6 rounded-xl border-2 p-4 flex items-center justify-between gap-4 ${
            stats.fiscal_pending > 0
              ? "bg-amber-50 border-amber-300"
              : "bg-emerald-50 border-emerald-300"
          }`}
          data-testid="fiscal-auto-update-banner"
        >
          <div className="flex items-start gap-3">
            <AlertTriangle
              className={`w-5 h-5 mt-0.5 flex-shrink-0 ${
                stats.fiscal_pending > 0 ? "text-amber-600" : "text-emerald-600"
              }`}
            />
            <div>
              <div className={`text-sm font-semibold ${stats.fiscal_pending > 0 ? "text-amber-900" : "text-emerald-900"}`}>
                {stats.fiscal_pending > 0
                  ? `${stats.fiscal_pending} atualização(ões) fiscal(is) pendente(s) de aprovação`
                  : "Tabelas IRS, IRS Jovem e SS alinhadas com o canonical"}
              </div>
              <div className="text-xs text-slate-700 mt-0.5">
                O sistema verifica automaticamente todos os dias às 06:00 (Lisboa) contra a fonte oficial e cria versões pendentes para o admin rever antes de entrarem em produção. Simulações antigas continuam a usar as tabelas do ano em que foram criadas.
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <Button
              size="sm"
              variant="outline"
              onClick={runFiscalCheck}
              disabled={fiscalChecking}
              className="h-8"
              data-testid="fiscal-check-now-btn"
              title="Correr um check imediato contra o canonical"
            >
              {fiscalChecking ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1.5" />}
              Verificar agora
            </Button>
            {stats.fiscal_pending > 0 && (
              <Button
                size="sm"
                onClick={applyAllFiscal}
                disabled={fiscalChecking}
                className="h-8 bg-amber-600 text-white hover:bg-amber-700"
                data-testid="fiscal-apply-all-btn"
              >
                Aprovar {stats.fiscal_pending}
              </Button>
            )}
          </div>
        </div>
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
            {menuItems.map((item) => (
              <Link key={item.href} to={item.href}>
                <Card className="bg-white border border-border/60 shadow-sm hover:shadow-md hover:border-slate-300 transition-all duration-200 hover:-translate-y-1 cursor-pointer h-full" data-testid={`card-${item.title.toLowerCase().replace(/\s+/g, '-')}`}>
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className={`w-12 h-12 rounded-xl ${item.color} flex items-center justify-center`}>
                        <item.icon className="w-6 h-6 text-white" />
                      </div>
                      <span className="font-mono font-bold text-3xl text-foreground tabular-nums">
                        {item.count}
                      </span>
                    </div>
                    <h3 className="font-manrope font-bold text-lg text-foreground">{item.title}</h3>
                    <p className="text-sm text-muted-foreground mt-1">{item.description}</p>
                    <div className="flex items-center text-sm text-muted-foreground mt-4 group-hover:text-foreground">
                      <span>Gerir</span>
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminDashboard;
